import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { useCreateComplaint } from "@/hooks/useComplaints";
import { useActiveEmployees } from "@/hooks/useEmployees";
import { CustomerCombobox } from "./CustomerCombobox";
import { complaintFormSchema, ComplaintFormValues } from "./complaint.schemas";
import { isAxiosError } from "axios";

interface ComplaintFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ComplaintFormDialog({ open, onOpenChange }: ComplaintFormDialogProps) {
  const { toast } = useToast();
  const createMutation = useCreateComplaint();
  const { data: employees } = useActiveEmployees();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ComplaintFormValues>({
    resolver: zodResolver(complaintFormSchema),
    defaultValues: { priority: "MEDIUM", customerId: "" },
  });

  const onSubmit = async (values: ComplaintFormValues) => {
    try {
      await createMutation.mutateAsync({
        customerId: values.customerId,
        productName: values.productName,
        brand: values.brand,
        model: values.model,
        serialNumber: values.serialNumber,
        description: values.description,
        priority: values.priority,
        assignedEmployeeId: values.assignedEmployeeId || undefined,
        expectedCompletionDate: values.expectedCompletionDate || undefined,
        internalNotes: values.internalNotes,
      });
      toast({ title: "Complaint created", variant: "success" });
      reset({ priority: "MEDIUM", customerId: "" });
      onOpenChange(false);
    } catch (err) {
      const message = isAxiosError(err)
        ? (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message
        : undefined;
      toast({ title: "Could not create complaint", description: message, variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New complaint</DialogTitle>
          <DialogDescription>Log a new customer complaint or service request.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label>Customer</Label>
            <Controller
              control={control}
              name="customerId"
              render={({ field }) => (
                <CustomerCombobox value={field.value} onChange={field.onChange} error={errors.customerId?.message} />
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="productName">Product name</Label>
              <Input id="productName" {...register("productName")} />
              {errors.productName && <p className="text-xs text-destructive">{errors.productName.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="brand">Brand (optional)</Label>
              <Input id="brand" {...register("brand")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="model">Model (optional)</Label>
              <Input id="model" {...register("model")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="serialNumber">Serial number (optional)</Label>
              <Input id="serialNumber" className="font-data" {...register("serialNumber")} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Issue description</Label>
            <textarea
              id="description"
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              {...register("description")}
            />
            {errors.description && <p className="text-xs text-destructive">{errors.description.message}</p>}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="priority">Priority</Label>
              <select
                id="priority"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                {...register("priority")}
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assignedEmployeeId">Assign to (optional)</Label>
              <select
                id="assignedEmployeeId"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                {...register("assignedEmployeeId")}
              >
                <option value="">Unassigned</option>
                {employees?.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.fullName}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="expectedCompletionDate">Expected completion</Label>
              <Input id="expectedCompletionDate" type="date" {...register("expectedCompletionDate")} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="internalNotes">Internal notes (optional)</Label>
            <textarea
              id="internalNotes"
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              {...register("internalNotes")}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create complaint
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
